/**
 * This file is part of an epic that is currently on hold and is not being used in the application.
 * Please refer to the README.md file in the root of the PDFRenderer directory for more information.
 */
import { Image, Path, StyleSheet, Svg, Text, View } from "@react-pdf/renderer";
import React, { useEffect, useState } from "react";

import { Style } from "@react-pdf/types";

declare global {
	interface Window {
		PERFORMYARD_MEDIA_CDN: string;
	}
}

const MEDIA_CDN = window?.PERFORMYARD_MEDIA_CDN || "https://performyard-content.performyard.com/";
const ANONYMOUS_AVATAR_URL = `${MEDIA_CDN}static/img/anonymous.png`;

const imageCache: Record<string, SvgImageData> = {};

type SvgImageData = {
	contentType: string;
	color: string | null;
	textContent: string | null;
	textColor: string | null;
};

/**
 * Fetches the SVG image data from the given URL.
 * This function is used to determine the color and text content of the SVG image.
 * It's needed because the Image component from @react-pdf/renderer doesn't support SVG images,
 * so we essentially need to parse the SVG image and use that info to create our own in react-pdf.
 */
const fetchSvgImageData = async (url: string) => {
	// Save ourselves from making the same request multiple times.
	if (imageCache[url]) {
		return imageCache[url];
	}

	const response = await fetch(url);
	const contentType = response.headers.get("content-type");

	let imageData: SvgImageData;

	// If the content type is an SVG image, we parse the SVG to get the color and text content.
	// Otherwise, we don't need to do anything. We'll have a link to a PNG or JPEG image, which is supported by react-pdf.
	if (contentType === "image/svg+xml") {
		const text = await response.text();
		const parser = new DOMParser();
		const svgDoc = parser.parseFromString(text, "image/svg+xml");
		const rect = svgDoc.querySelector("rect");
		const color = rect ? rect.getAttribute("fill") : "none";
		const textElement = svgDoc.querySelector("text");
		const textContent = textElement ? textElement.textContent : "";
		const textColor = textElement ? textElement.getAttribute("fill") : "none";
		imageData = { contentType, color, textContent, textColor };
	} else {
		imageData = { contentType: "validImage", color: null, textContent: null, textColor: null };
	}

	imageCache[url] = imageData;
	return imageData;
};

const styles = StyleSheet.create({
	avatar: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	image: {
		objectFit: "cover",
		borderRadius: 9999,
	},
});

type AvatarProps = {
	id?: string | null;
	size?: number;
	style?: Style;
};

/**
 * A component for displaying user avatars.
 * If an id is provided, the component will render the user's avatar.
 * If no id is provided, the component will render an anonymous avatar.
 */
export const Avatar = (props: AvatarProps) => {
	const { id, size = 100, style = {} } = props;

	const [imageData, setImageData] = useState<SvgImageData>();

	const src = id ? `${MEDIA_CDN}avatar/${id}/` : ANONYMOUS_AVATAR_URL;

	useEffect(() => {
		fetchSvgImageData(src).then(setImageData);
	}, [src]);

	const imageStyleProps = {
		...(size ? { height: size, width: size } : {}),
		...style,
	};

	// If we haven't fetched the image data yet, we don't render anything.
	if (!imageData) {
		return null;
	}

	// If the image is an SVG, we need to use the Svg component from react-pdf to render it.
	if (imageData?.contentType === "image/svg+xml") {
		return (
			<Svg data-testid="svg-avatar" viewBox="0 0 32 32" width={size} height={size}>
				<Path
					d="M16,2 C23.7319865,2 30,8.2680135 30,16 C30,23.7319865 23.7319865,30 16,30 C8.2680135,30 2,23.7319865 2,16 C2,8.2680135 8.2680135,2 16,2 Z"
					fill={imageData.color ?? "none"}
				/>
				<Text
					fill={imageData.textColor ?? "none"}
					x="16"
					y="22"
					textAnchor="middle"
					style={{ fontSize: 15, fontFamily: "Noto Sans", fontWeight: "semibold" }}
				>
					{imageData.textContent}
				</Text>
			</Svg>
		);
	}

	// Otherwise, we can use the Image component from react-pdf to render the image.
	return (
		<View data-testid="image-avatar" style={[styles.avatar, imageStyleProps]}>
			<Image style={[styles.image]} src={src} />
		</View>
	);
};
